<template>
  <div class="custom-timer" v-if="!session.isSpectator">
    <!-- Collapsed view - show timer only -->
    <div class="timer-display" @click="isExpanded = !isExpanded" :class="{ warning: isWarning, expired: isExpired }">
      <font-awesome-icon :icon="isRunning ? 'pause' : 'play'" class="status-icon" />
      <span class="time">{{ displayTime }}</span>
    </div>

    <!-- Expanded controls -->
    <transition name="slide-down">
      <div class="timer-controls" v-if="isExpanded">
        <div class="time-setter">
          <button @click="adjustTime(-1)" :disabled="isRunning">
            <font-awesome-icon icon="minus" />
          </button>
          <input 
            type="number" 
            v-model.number="minutesInput" 
            @change="setCustomTime"
            :disabled="isRunning"
            min="1"
            max="999"
            :placeholder="$t('timer.minutes')"
          />
          <span class="unit">{{ $t('timer.minutes') }}</span>
          <button @click="adjustTime(1)" :disabled="isRunning">
            <font-awesome-icon icon="plus" />
          </button>
        </div>
        
        <div class="button-group">
          <button class="btn-start" @click="toggleTimer" :class="{ active: isRunning }">
            <font-awesome-icon :icon="isRunning ? 'pause' : 'play'" />
            {{ isRunning ? $t('timer.pause') : $t('timer.start') }}
          </button>
          <button class="btn-reset" @click="resetTimer">
            <font-awesome-icon icon="redo" />
            {{ $t('timer.reset') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "Timer",
  computed: {
    ...mapState(["session"]),
    displayTime() {
      const minutes = Math.floor(this.remainingSeconds / 60);
      const seconds = this.remainingSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    isWarning() {
      // Warning when less than 1 minute left
      return this.remainingSeconds <= 60 && this.remainingSeconds > 0;
    },
    isExpired() {
      return this.remainingSeconds <= 0 && this.totalSeconds > 0;
    },
  },
  data() {
    return {
      isExpanded: false,
      isRunning: false,
      remainingSeconds: 0,
      totalSeconds: 0,
      minutesInput: 5, // Default 5 minutes
      timerInterval: null,
    };
  },
  mounted() {
    // Load saved timer state from localStorage
    const saved = localStorage.getItem("customTimer");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        this.minutesInput = data.minutes || 5;
        this.totalSeconds = data.totalSeconds || 0;
        this.remainingSeconds = data.remainingSeconds || 0;
      } catch (e) {
        console.error("Failed to load timer state:", e);
      }
    }
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    toggleTimer() {
      if (this.isRunning) {
        this.pauseTimer();
      } else {
        this.startTimer();
      }
    },
    startTimer() {
      if (this.remainingSeconds <= 0) {
        // If timer is expired or not set, use minutesInput
        this.totalSeconds = this.minutesInput * 60;
        this.remainingSeconds = this.totalSeconds;
      }
      
      this.isRunning = true;
      this.timerInterval = setInterval(() => {
        if (this.remainingSeconds > 0) {
          this.remainingSeconds--;
          this.saveState();
          
          // Play sound when timer expires
          if (this.remainingSeconds === 0) {
            this.playNotification();
          }
        } else {
          this.pauseTimer();
        }
      }, 1000);
    },
    pauseTimer() {
      this.isRunning = false;
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      this.saveState();
    },
    resetTimer() {
      this.pauseTimer();
      this.totalSeconds = this.minutesInput * 60;
      this.remainingSeconds = this.totalSeconds;
      this.saveState();
    },
    setCustomTime() {
      if (!this.isRunning) {
        this.totalSeconds = this.minutesInput * 60;
        this.remainingSeconds = this.totalSeconds;
        this.saveState();
      }
    },
    adjustTime(delta) {
      if (!this.isRunning) {
        this.minutesInput = Math.max(1, Math.min(999, this.minutesInput + delta));
        this.setCustomTime();
      }
    },
    saveState() {
      localStorage.setItem("customTimer", JSON.stringify({
        minutes: this.minutesInput,
        totalSeconds: this.totalSeconds,
        remainingSeconds: this.remainingSeconds,
      }));
    },
    playNotification() {
      // Play a notification sound when timer expires
      const audio = document.querySelector('audio[src*="countdown"]');
      if (audio) {
        audio.play().catch(e => console.log("Could not play notification:", e));
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../vars.scss";

.custom-timer {
  position: fixed;
  top: 10px;
  right: 170px; // Move left to avoid blocking vote history and menu buttons
  z-index: 75; // Same as #controls to align with other buttons
  font-family: "Roboto Condensed", sans-serif;

  .timer-display {
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);

    &:hover {
      background: rgba(0, 0, 0, 0.95);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.6);
    }

    &.warning {
      border-color: #ff9800;
      animation: pulse-warning 1s ease-in-out infinite;
      
      .time {
        color: #ff9800;
      }
    }

    &.expired {
      border-color: #f44336;
      animation: pulse-danger 1s ease-in-out infinite;
      
      .time {
        color: #f44336;
      }
    }

    .status-icon {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
    }

    .time {
      font-size: 20px;
      font-weight: 700;
      color: white;
      min-width: 60px;
      text-align: center;
      letter-spacing: 1px;
    }
  }

  .timer-controls {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    background: rgba(0, 0, 0, 0.95);
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    padding: 12px;
    min-width: 200px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);

    .time-setter {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 10px;

      button {
        width: 28px;
        height: 28px;
        border: none;
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.2);
        }

        &:active:not(:disabled) {
          transform: scale(0.95);
        }

        &:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        svg {
          font-size: 12px;
        }
      }

      input {
        width: 50px;
        padding: 4px 8px;
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        transition: all 0.2s ease;

        &:focus {
          outline: none;
          border-color: #4a90e2;
          background: rgba(255, 255, 255, 0.15);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Remove spinner arrows */
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        -moz-appearance: textfield;
      }

      .unit {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .button-group {
      display: flex;
      gap: 6px;

      button {
        flex: 1;
        padding: 6px 12px;
        border: none;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;

        svg {
          font-size: 11px;
        }

        &.btn-start {
          background: #4caf50;
          color: white;

          &:hover {
            background: #45a049;
          }

          &:active {
            transform: scale(0.95);
          }

          &.active {
            background: #ff9800;

            &:hover {
              background: #f57c00;
            }
          }
        }

        &.btn-reset {
          background: #f44336;
          color: white;

          &:hover {
            background: #da190b;
          }

          &:active {
            transform: scale(0.95);
          }
        }
      }
    }
  }
}

@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  50% {
    box-shadow: 0 4px 16px rgba(255, 152, 0, 0.6);
  }
}

@keyframes pulse-danger {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  50% {
    box-shadow: 0 4px 16px rgba(244, 67, 54, 0.6);
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Mobile styles */
@media screen and (max-width: 768px) {
  .custom-timer {
    top: 60px; // Below the menu button and other controls
    right: 10px;

    .timer-display {
      padding: 6px 12px;

      .time {
        font-size: 16px;
        min-width: 50px;
      }
    }

    .timer-controls {
      min-width: 180px;
    }
  }
}

/* Tablet and smaller desktop */
@media screen and (max-width: 1024px) and (min-width: 769px) {
  .custom-timer {
    right: 120px; // Adjust for medium screens
  }
}
</style>
